import { Box } from "@mui/material";
import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import Typography from "@mui/material/Typography";
import { SxProps, Theme } from "@mui/system";
import { RootState } from "../../redux/store.ts";
import { useSelector } from "react-redux";

function addTargetBlank(html: string) {
  return html.replace(
    /<a\s/gi,
    '<a target="_blank" rel="noopener noreferrer" ',
  );
}
interface PropType {
  style?: CSSProperties;
  value: string;
  type?:
    | "mainTitle"
    | "subTitle"
    | "title"
    | "subText"
    | "mainButton"
    | "subButton"
    | "dialogTitle"
    | "dialogText"
    | "subTextBold"
    | "mainBlack"
    | "mainGray"
    | "stepText"
    | "stepNumber"
    | "mainTitleBlack"
    | "grayBlack"
    | "labelText"
    | "simpleTitle"
    | "codeText"
    | "redCode"
    | "greenCode"
    | "stepCode"
    | "simpleBlue"
    | "bannerText";
  dyslexia?: boolean;
  contrast?: boolean;
  diopteres?: boolean;
  sx?: SxProps<Theme>;
  noTranslation?: boolean;
}

const SimpleText = ({
  value,
  style = {},
  type = "mainTitle",
  sx = {},
  noTranslation = false,
}: PropType) => {
  const { t } = useTranslation();
  const { dislexia, diopteres, contrast } = useSelector(
    (state: RootState) => state?.settings?.data?.disability,
  );

  // Helper function to apply 20% increase in font size
  const applyDioptry = (fontSize: string) => {
    const size = parseFloat(fontSize);
    return `${(size * 1.2).toFixed(2)}px`;
  };

  const mainTitle = {
    fontFamily: "Inter",
    fontWeight: 600,
    fontSize: {
      sx: diopteres ? applyDioptry("24") : "24px",
      sm: diopteres ? applyDioptry("38") : "38px",
      lg: diopteres ? applyDioptry("40") : "40px",
    },
    color: contrast ? "#000000" : "#001E96",
  };

  const subTitle = {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: {
      sx: diopteres ? applyDioptry("10") : "10px",
      sm: diopteres ? applyDioptry("15") : "15px",
      lg: diopteres ? applyDioptry("18") : "18px",
    },
    color: contrast ? "#000000" : "#021049",
  };

  const title = {
    fontFamily: "Inter",
    fontWeight: 600,
    fontSize: {
      sx: diopteres ? applyDioptry("20") : "20px",
      sm: diopteres ? applyDioptry("26") : "26px",
      lg: diopteres ? applyDioptry("30") : "32px",
    },
    color: contrast ? "#000000" : "#001E96",
  };
  const subText = {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: {
      sx: diopteres ? applyDioptry("10") : "10px",
    },
    color: contrast ? "#000000" : "#606060",
  };

  const mainButton = {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: {
      sx: diopteres ? applyDioptry("14") : "14px",
      sm: diopteres ? applyDioptry("16") : "16px",
      lg: diopteres ? applyDioptry("18") : "18px",
    },
    color: "#ffff",
  };

  const dialogTitle = {
    fontFamily: "Inter",
    fontWeight: 600,
    fontSize: {
      sx: diopteres ? applyDioptry("16") : "18px",
      sm: diopteres ? applyDioptry("20") : "25px",
      lg: diopteres ? applyDioptry("24") : "30px",
    },
    color: contrast ? "#000000" : "#001E96",
  };
  const dialogText = {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: {
      sx: diopteres ? applyDioptry("16") : "16px",
      sm: diopteres ? applyDioptry("20") : "20px",
      lg: diopteres ? applyDioptry("24") : "24px",
    },
    color: contrast ? "#000000" : "#2D2D2DB2",
  };
  const subTextBold = {
    fontFamily: "Inter",
    fontWeight: 600,
    fontSize: {
      sx: diopteres ? applyDioptry("8") : "8px",
      sm: diopteres ? applyDioptry("12") : "12px",
      lg: diopteres ? applyDioptry("18") : "18px",
    },
    color: contrast ? "#000000" : "#021049",
  };
  const mainBlack = {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: {
      sx: diopteres ? applyDioptry("12") : "12px",
      sm: diopteres ? applyDioptry("16") : "16px",
      lg: diopteres ? applyDioptry("18") : "18px",
    },
    color: "#000000",
  };
  const mainGray = {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: {
      sx: diopteres ? applyDioptry("12") : "12px",
      sm: diopteres ? applyDioptry("16") : "16px",
      lg: diopteres ? applyDioptry("18") : "18px",
    },
    color: contrast ? "#000000" : "#000000B2",
  };
  const stepText = {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: {
      sx: diopteres ? applyDioptry("11") : "11px",
      sm: diopteres ? applyDioptry("14") : "14px",
      lg: diopteres ? applyDioptry("16") : "16px",
    },
    color: contrast ? "#000000" : "#00000080",
  };
  const stepNumber = {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: {
      sx: diopteres ? applyDioptry("11") : "11px",
      sm: diopteres ? applyDioptry("14") : "14px",
      lg: diopteres ? applyDioptry("16") : "16px",
    },
    color: contrast ? "#fff" : "#001E96",
  };
  const mainTitleBlack = {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: {
      sx: diopteres ? applyDioptry("16") : "16px",
      sm: diopteres ? applyDioptry("18") : "18px",
      lg: diopteres ? applyDioptry("20") : "20px",
    },
    color: "#000000",
  };
  const grayBlack = {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: {
      sx: diopteres ? applyDioptry("12") : "12px",
      sm: diopteres ? applyDioptry("14") : "14px",
      lg: diopteres ? applyDioptry("16") : "16px",
    },
    color: "#000000",
  };
  const labelText = {
    fontFamily: "Inter",
    fontWeight: 700,
    fontSize: {
      sx: diopteres ? applyDioptry("11") : "11px",
      sm: diopteres ? applyDioptry("14") : "14px",
      lg: diopteres ? applyDioptry("16") : "16px",
    },
    color: contrast ? "#000000" : "#00000099",
  };
  const simpleTitle = {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: {
      sx: diopteres ? applyDioptry("16") : "16px",
      sm: diopteres ? applyDioptry("20") : "20px",
      lg: diopteres ? applyDioptry("24") : "24px",
    },
    color: contrast ? "#000000" : "#001E96",
  };
  const codeText = {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: {
      sx: diopteres ? applyDioptry("12") : "12px",
      sm: diopteres ? applyDioptry("16") : "16px",
      lg: diopteres ? applyDioptry("18") : "18px",
    },
    color: contrast ? "#000000" : "#001E96",
  };
  const redCode = {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: {
      sx: diopteres ? applyDioptry("12") : "12px",
      sm: diopteres ? applyDioptry("16") : "16px",
      lg: diopteres ? applyDioptry("18") : "18px",
    },
    color: contrast ? "#000000" : "#C82828",
  };
  const greenCode = {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: {
      sx: diopteres ? applyDioptry("12") : "12px",
      sm: diopteres ? applyDioptry("16") : "16px",
      lg: diopteres ? applyDioptry("18") : "18px",
    },
    color: contrast ? "#000000" : "#3EC828",
  };
  const stepCode = {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: {
      sx: diopteres ? applyDioptry("11") : "11px",
      sm: diopteres ? applyDioptry("14") : "14px",
      lg: diopteres ? applyDioptry("16") : "16px",
    },
    color: contrast ? "#000000" : "#001E96",
  };

  const simpleBlue = {
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: {
      sx: diopteres ? applyDioptry("12") : "12px",
      sm: diopteres ? applyDioptry("16") : "16px",
      lg: diopteres ? applyDioptry("18") : "18px",
    },
    color: contrast ? "#000000" : "#001E96",
  };
  const bannerText = {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: {
      xs: diopteres ? applyDioptry("11") : "11px",
      sm: diopteres ? applyDioptry("14") : "14px",
      lg: diopteres ? applyDioptry("15") : "15px",
    },
    padding: {
      xs: "3px",
      sm: "7px",
      lg: "10px",
    },
    color: "#fff",
    textAlign: "center",
    background: "#001E96",
    zIndex: 999,
  };
  return (
    <Box
      sx={[
        ...(Array.isArray(sx) ? sx : [sx]),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Typography
        className="input-label"
        variant="body1"
        sx={{
          ...(type === "mainTitle" && mainTitle),
          ...(type === "subTitle" && subTitle),
          ...(type === "title" && title),
          ...(type === "subText" && subText),
          ...(type === "mainButton" && mainButton),
          ...(type === "dialogTitle" && dialogTitle),
          ...(type === "dialogText" && dialogText),
          ...(type === "subTextBold" && subTextBold),
          ...(type === "mainBlack" && mainBlack),
          ...(type === "mainGray" && mainGray),
          ...(type === "stepText" && stepText),
          ...(type === "stepNumber" && stepNumber),
          ...(type === "mainTitleBlack" && mainTitleBlack),
          ...(type === "grayBlack" && grayBlack),
          ...(type === "labelText" && labelText),
          ...(type === "simpleTitle" && simpleTitle),
          ...(type === "codeText" && codeText),
          ...(type === "greenCode" && greenCode),
          ...(type === "redCode" && redCode),
          ...(type === "stepCode" && stepCode),
          ...(type === "simpleBlue" && simpleBlue),
          ...(type === "bannerText" && bannerText),
          letterSpacing: dislexia ? "0.1em" : "normal",
          ...style,
        }}
      >
        {typeof value === "string" && /<\/?[a-z][\s\S]*>/i.test(value) ? (
          <Typography
            sx={{
              ...(type === "mainTitle" && mainTitle),
              ...(type === "subTitle" && subTitle),
              ...(type === "title" && title),
              ...(type === "subText" && subText),
              ...(type === "mainButton" && mainButton),
              ...(type === "dialogTitle" && dialogTitle),
              ...(type === "dialogText" && dialogText),
              ...(type === "subTextBold" && subTextBold),
              ...(type === "mainBlack" && mainBlack),
              ...(type === "mainGray" && mainGray),
              ...(type === "stepText" && stepText),
              ...(type === "stepNumber" && stepNumber),
              ...(type === "mainTitleBlack" && mainTitleBlack),
              ...(type === "grayBlack" && grayBlack),
              ...(type === "labelText" && labelText),
              ...(type === "simpleTitle" && simpleTitle),
              ...(type === "codeText" && codeText),
              ...(type === "greenCode" && greenCode),
              ...(type === "redCode" && redCode),
              ...(type === "stepCode" && stepCode),
              ...(type === "simpleBlue" && simpleBlue),
              ...(type === "bannerText" && bannerText),
              letterSpacing: dislexia ? "0.1em" : "normal",
              ...style,
            }}
            dangerouslySetInnerHTML={{ __html: addTargetBlank(value) }}
          />
        ) : noTranslation ? (
          value
        ) : (
          t(value)
        )}
      </Typography>
    </Box>
  );
};

export default SimpleText;
